import { Switch } from "@workspace/web-ui/components/switch";
import { Label } from "@workspace/web-ui/components/label";
import { getRouteApi } from "@tanstack/react-router";
import { cn, getApiProgram } from "~/lib/utils";
import { useResolveEncounterMutation } from "~/features/dashboard/pages/encounters/api/mutations";
import { toast } from "sonner";

type ResolveToggleProps = {
  encounterId?: string;
  className?: string;
  label?: string;
  recommendedTasks?: any[];
  disabled?: boolean;
  disabledReason?: string;
  onSuccess?: () => void;
};

export const ResolveToggle = ({
  encounterId,
  className,
  label = "Resolve",
  recommendedTasks,
  disabled,
  disabledReason,
  onSuccess,
}: ResolveToggleProps) => {
  const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
  const { program } = routeApi.useParams();
  const apiProgram = getApiProgram(program ?? "");
  const { mutate: resolveEncounter, isPending: isResolving } = useResolveEncounterMutation();

  if (program !== "urgent-care" || !encounterId) {
    return null;
  }

  const isResolved =
    Array.isArray(recommendedTasks) &&
    recommendedTasks.some(
      (task) =>
        task.type === "medication_payment" || task.type === "no_action_needed"
    );

  const switchComponent = (
    <Switch
      id={`resolve-toggle-${encounterId}`}
      disabled={isResolving || isResolved}
      checked={isResolved}
      className={cn(
        isResolving && "data-[state=unchecked]:bg-primary",
        isResolved && "disabled:opacity-100",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      onCheckedChange={(checked) => {
        if (disabled) {
          if (disabledReason) {
            toast.error(disabledReason);
          }
          return;
        }

        if (checked) {
          resolveEncounter({
            encounter_id: encounterId,
            program: apiProgram,
          }, {
            onSuccess: () => {
              onSuccess?.();
            }
          });
        }
      }}
    />
  );

  return (
    <div className={["flex items-center gap-2", className].filter(Boolean).join(" ")}>
      <Label
        htmlFor={`resolve-toggle-${encounterId}`}
        className="text-sm font-medium text-[#1A202C] cursor-pointer"
      >
        {label}
      </Label>
      {switchComponent}
    </div>
  );
};

